import { ReactElement, useMemo } from "react";
import Menu from "src/assets/icons-redesign/menu.svg?react";
import { Skeleton } from "src/components/ui/skeleton";
import { orderBy, partial} from "lodash";
import { AnswerSource, Question, QuestionStats, SurveyStatus } from "src/redux/survey/survey.types";
import { answerTitle, percentageKey } from "../../view-survey/components/results-question.helpers";

type QuestionProps =
  | { variant: "skeleton-summary" }
  | { variant: "skeleton" }
  | {
    variant: "summary",
    isActive: boolean,
    handleClick: (index: string) => void,
  }
  | {
    variant: "question",
    key: string;
    isActive: boolean;
    index: number;
    handleClick: (index: string) => void;
    question: Question;
    questionStats: QuestionStats;
    surveyStatus: SurveyStatus;
  };

const ResultsSidebarQuestion = (props: QuestionProps): ReactElement => {
  const { variant } = props;

  if (variant === "skeleton-summary") {
    return (
      <div className="flex w-full my-2" >
        <div className="flex px-6 gap-2 w-full" >
          <Skeleton className="shrink-0 size-6" />
          <Skeleton className="h-6 w-full" />
        </div>
      </div>
    );
  }

  if (variant === "skeleton") {
    return (
      <div className="flex w-full my-2" >
        <div className="flex px-6 gap-2 w-full items-center" >
          <Skeleton className="shrink-0 size-6" />
          <div className="flex flex-col gap-2 w-full">
            <Skeleton className="h-6 w-full" />
            <Skeleton className="h-6 w-full" />
          </div>
        </div>
      </div>
    );
  }

  const { handleClick, isActive } = props;

  // todo use real colors
  const borderClass = isActive ? 'border-[#3257FB]' : 'border-transparent';

  if (variant === "summary") {
    return (
      <div
        className={`flex w-full cursor-pointer shrink my-2 items-center border-l-2 ${borderClass}`}
        onClick={partial(handleClick, "summary")}
      >
        <div className="flex px-6 gap-2 min-w-0 items-center" >
          <div className="flex shrink-0 rounded-full size-6 bg-blue-light-500 text-white justify-center items-center">
            <Menu className="size-3 stroke-white" />
          </div>
          <div className="flex min-w-0 flex-col text-base">
            Summary
          </div>
        </div>
      </div>
    );
  }

  const { questionStats, question, index, surveyStatus } = props;

  const { maxTitle, maxPercent } = useMemo(() => {
    // We shouldn't get empty answer length arrays from the server, but it's technically possible
    if (!questionStats?.answers?.length) {
      return { maxPercent: 0, maxTitle: '' };
    }

    const [topAnswer] = orderBy(questionStats.answers, percentageKey(AnswerSource.All), 'desc');

    return {
      maxTitle: answerTitle(topAnswer.title, index, surveyStatus),
      maxPercent: Math.round(topAnswer.totalPercentage),
    };
  }, [questionStats]);

  return (
    <div
      className={`flex cursor-pointer shrink my-2 items-center border-l-2 ${borderClass}`}
      key={question.title}
      onClick={partial(handleClick, String(index))}
    >
      <div className="flex px-6 gap-2 min-w-0 items-center" >
        <div className="flex shrink-0 rounded-full size-6 bg-blue-light-500 text-white justify-center items-center">
          <span>
            {index + 1}
          </span>
        </div>
        <div className="flex min-w-0 flex-col text-base">
          <span className="truncate">
            {question.title}
          </span>
          {Boolean(maxPercent) && (
            <span className="flex gap-1">
              <span className="truncate">
                {maxTitle}
              </span>
              <span>
                -
              </span>
              <span>
                {maxPercent}
                %
              </span>
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResultsSidebarQuestion;
